import { List, ListItem, Portal } from "@chakra-ui/core";
import { Popover, PopoverBody, PopoverContent, PopoverHeader } from "@chakra-ui/popover";
import _mapValues from "lodash.mapvalues";
import { Box, FormField, Icon, Stack, Text } from "pws-design-system/design-system/";
import React, { useRef, useState } from "react";
import { object, string } from "yup";
import _debounce from "lodash.debounce";
import { ThemeContainer } from "../../../common/hooks/useTheme";

const paramSchema = object().shape({
  id: string().required(),
  token: string().required()
});

const passwordTests = {
  length: (password: string) => password.length >= 8,
  lowercase: (password: string) => /(?=.*[a-z])/.test(password),
  uppercase: (password: string) => /(?=.*[A-Z])/.test(password),
  numberOrSpecialCharacter: (password: string) => {
    return /(?=.*\d)/.test(password) || /[-+_!@#$%^&*.,?]/.test(password);
  }
} as any;

const errorMap = {
  validation_error: {
    message: "Your submission was invalid. Please try again."
  },
  unauthorized: {
    message: () => "Wrong credentials. Please try again."
  },
  unknown: {
    message: () => ""
  }
};

const ValidationIcon = props => {
  const { valid } = props;
  if (valid) {
    return <Icon name="check-circle" color="green.400" />;
  }
  return <Icon name="check-circle" color="gray.300" />;
};

export const PasswordField = () => {
  const { theme } = ThemeContainer.useContainer();
  const [passwordValidation, setPasswordValidation] = useState(
    _mapValues(passwordTests, () => false)
  );
  const [fieldIsFocused, setFieldIsFocused] = useState(false);
  const initialFocusRef = useRef(null);

  const onFocus = ev => {
    setFieldIsFocused(true);
  };

  const onBlur = ev => {
    setFieldIsFocused(false);
  };

  const onChange = (event: React.SyntheticEvent) => {
    event.persist();
    if (!event.target) {
      return;
    }
    const value = (event.target as HTMLInputElement).value;
    const validation = _mapValues(passwordTests, (test: (password: string) => boolean) =>
      test(value)
    );
    setPasswordValidation(validation);
  };

  const debouncedOnChange = _debounce(onChange, 100, { leading: true });

  return (
    <>
      <div onFocus={onFocus} onBlur={onBlur}>
        <FormField
          name="password"
          label="Password"
          ref={initialFocusRef}
          placeholder=""
          // onFocus={onFocus}
          // onBlur={onBlur}
          field={{
            type: "password",
            variant: "flushed",
            rightElement: <Icon name="lock" color="gray.300" />,
            background: theme.colors.bg.base.primary
          }}
          required
          onChange={debouncedOnChange}
        />
      </div>
      <Box position="absolute" mt="10px" color="black" width="300px" tabIndex={-1}>
        <Popover isOpen={fieldIsFocused} autoFocus={false} returnFocusOnClose={false}>
          <PopoverContent zIndex={4} background="white" p={3}>
            {/* <PopoverArrow /> */}
            <PopoverBody>
              <PopoverHeader py={2}>
                <Text variant="caption1">Your Password must have at least:</Text>
              </PopoverHeader>
              <PopoverBody boxShadow="md" py={2}>
                <Stack spacing={1}>
                  <List mt={0}>
                    <ListItem>
                      <Stack isInline={true} spacing={2} align="flex-end">
                        <Box>
                          <ValidationIcon valid={passwordValidation.length} />
                        </Box>
                        <Text variant="caption1">eight characters</Text>
                      </Stack>
                    </ListItem>
                    <ListItem>
                      <Stack isInline={true} spacing={2} align="flex-end">
                        <Box>
                          <ValidationIcon valid={passwordValidation.lowercase} />{" "}
                        </Box>
                        <Text variant="caption1">one lowercase letter</Text>
                      </Stack>
                    </ListItem>
                    <ListItem>
                      <Stack isInline={true} spacing={2} align="flex-end">
                        <Box>
                          <ValidationIcon valid={passwordValidation.uppercase} />
                        </Box>
                        <Text variant="caption1">one uppercase letter</Text>
                      </Stack>
                    </ListItem>
                    <ListItem>
                      <Stack isInline={true} spacing={2} align="flex-end">
                        <Box>
                          <ValidationIcon valid={passwordValidation.numberOrSpecialCharacter} />
                        </Box>
                        <Text variant="caption1">one number or special character</Text>
                      </Stack>
                    </ListItem>
                  </List>
                </Stack>
              </PopoverBody>
            </PopoverBody>
          </PopoverContent>
        </Popover>
      </Box>
    </>
  );
};
